import {
  BillingResponseSchema,
  CheckoutBodySchema,
  CheckoutResponseSchema,
  PortalResponseSchema,
  type BillingResponse,
  type CheckoutBody,
  type CheckoutResponse,
  type PortalResponse,
} from '../../src/shared/billing';
import { apiGet, apiPost } from './client';

/**
 * Typed SPA client for the workspace billing surface (ARCHITECTURE_PLAN §9):
 *   GET  /api/w/:slug/billing            — current plan + subscription + usage meters
 *   POST /api/w/:slug/billing/checkout   — create a Stripe Checkout session, return URL
 *   POST /api/w/:slug/billing/portal     — create a Stripe Billing Portal session, return URL
 *
 * Each response is validated against the shared zod schemas so a contract
 * drift between Worker and SPA fails loudly instead of rendering garbage (§3).
 * The billing surface is owner-only on the server (§6.3 `requireRole('owner')`);
 * the SPA additionally hides the route from non-owners (§10).
 */

/**
 * React Query key prefix for billing data. The `['billing', slug]` prefix is
 * intentionally shared with the realtime `usage.threshold` invalidation map
 * (see `app/realtime/useRealtime.ts`) so a threshold event refreshes the
 * meters without a manual reload.
 */
export const billingKeys = {
  all: (slug: string) => ['billing', slug] as const,
  detail: (slug: string) => ['billing', slug, 'detail'] as const,
};

/**
 * `GET /api/w/:slug/billing` — current plan, subscription state, and the
 * current-period usage counters vs the matching plan_limits row.
 */
export function fetchBilling(slug: string, signal?: AbortSignal): Promise<BillingResponse> {
  return apiGet(`/api/w/${slug}/billing`, BillingResponseSchema, signal);
}

/**
 * `POST /api/w/:slug/billing/checkout` — create a Stripe Checkout session for
 * the given target plan and return the hosted Stripe URL to redirect to.
 */
export function createCheckout(slug: string, body: CheckoutBody): Promise<CheckoutResponse> {
  const parsed = CheckoutBodySchema.parse(body);
  return apiPost(`/api/w/${slug}/billing/checkout`, parsed, CheckoutResponseSchema);
}

/**
 * `POST /api/w/:slug/billing/portal` — create a Stripe Billing Portal session
 * for managing the existing subscription (card, invoices, cancellation).
 */
export function createPortal(slug: string): Promise<PortalResponse> {
  return apiPost(`/api/w/${slug}/billing/portal`, {}, PortalResponseSchema);
}
